import { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Save, Plus, X, Palette } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardContent,
  Button,
  Input,
  Select,
  LoadingSpinner,
} from '../components/ui';
import { getSettings, updateSettings } from '../services/api';
import type { Settings as SettingsType } from '../types';

/** Generate a random vibrant hex colour. */
function randomColor(): string {
  const hue = Math.floor(Math.random() * 360);
  const s = 0.65 + Math.random() * 0.15; // 65-80%
  const l = 0.50 + Math.random() * 0.10; // 50-60%
  const h = hue / 360;
  const hue2rgb = (p: number, q: number, t: number) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
  const g = Math.round(hue2rgb(p, q, h) * 255);
  const b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

/** Return the saved colour or generate a new random one. */
function resolveCategoryColor(categoryColors: Record<string, string> | undefined, name: string): string {
  const saved = categoryColors?.[name];
  return saved && /^#[0-9a-fA-F]{6}$/.test(saved) ? saved : randomColor();
}

const currencies = [
  { value: 'INR', label: 'Indian Rupee (₹)' },
  { value: 'USD', label: 'US Dollar ($)' },
  { value: 'EUR', label: 'Euro (€)' },
  { value: 'GBP', label: 'British Pound (£)' },
  { value: 'JPY', label: 'Japanese Yen (¥)' },
  { value: 'AED', label: 'UAE Dirham (د.إ)' },
];

const dateFormats = [
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2024-01-15)' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (15/01/2024)' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (01/15/2024)' },
  { value: 'DD-MMM-YYYY', label: 'DD-MMM-YYYY (15-Jan-2024)' },
];

const months = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<SettingsType | null>(null);
  const [newCategory, setNewCategory] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [inrRates, setInrRates] = useState<Record<string, number>>({});

  useEffect(() => {
    fetchSettings();
    fetchInrRates();
  }, []);

  const fetchInrRates = async () => {
    const currenciesToFetch = currencies
      .map((currency) => currency.value)
      .filter((code) => code !== 'INR');

    const fetchRateToInr = async (code: string): Promise<number | null> => {
      try {
        const primaryResponse = await fetch(`https://api.frankfurter.app/latest?from=${code}&to=INR`);
        if (primaryResponse.ok) {
          const primaryData = await primaryResponse.json() as { rates?: Record<string, number> };
          const primaryRate = primaryData?.rates?.INR;
          if (typeof primaryRate === 'number') {
            return primaryRate;
          }
        }
      } catch {
      }

      try {
        const fallbackResponse = await fetch(`https://open.er-api.com/v6/latest/${code}`);
        if (!fallbackResponse.ok) return null;
        const fallbackData = await fallbackResponse.json() as { rates?: Record<string, number> };
        const fallbackRate = fallbackData?.rates?.INR;
        return typeof fallbackRate === 'number' ? fallbackRate : null;
      } catch {
        return null;
      }
    };

    const results = await Promise.all(
      currenciesToFetch.map(async (code) => {
        const rate = await fetchRateToInr(code);
        return typeof rate === 'number' ? { code, rate } : null;
      })
    );

    const rateMap = results.reduce<Record<string, number>>((acc, item) => {
      if (item) {
        acc[item.code] = item.rate;
      }
      return acc;
    }, {});

    setInrRates(rateMap);
  };

  const currencyOptions = currencies.map((currency) => {
    const liveRate = inrRates[currency.value];
    if (currency.value !== 'INR' && typeof liveRate === 'number') {
      return {
        ...currency,
        label: `${currency.label} (1 ${currency.value} = ${liveRate.toFixed(2)} INR today)`,
      };
    }
    return currency;
  });

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await getSettings();
      if (response.success) {
        // Auto-assign colours for categories that don't have one yet
        const cats = response.settings.categories || [];
        const existing = response.settings.categoryColors || {};
        let patched = false;
        const next = { ...existing };
        for (const cat of cats) {
          if (!next[cat] || !/^#[0-9a-fA-F]{6}$/.test(next[cat])) {
            next[cat] = randomColor();
            patched = true;
          }
        }
        if (patched) {
          response.settings.categoryColors = next;
        }
        setSettings(response.settings);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;
    
    try {
      setSaving(true);
      const response = await updateSettings(settings);
      if (response.success) {
        const latestSettings = await getSettings();
        if (latestSettings.success) {
          setSettings(latestSettings.settings);
        }
        setHasChanges(false);
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = <K extends keyof SettingsType>(key: K, value: SettingsType[K]) => {
    setSettings((prev) => {
      if (!prev) return prev;
      return { ...prev, [key]: value };
    });
    setHasChanges(true);
  };

  const addCategory = () => {
    if (!settings || !newCategory.trim()) return;
    if (settings.categories.includes(newCategory.trim())) return;

    const categoryName = newCategory.trim();
    updateSetting('categories', [...settings.categories, categoryName]);
    updateSetting('categoryColors', {
      ...(settings.categoryColors || {}),
      [categoryName]: randomColor(),
    });
    setNewCategory('');
  };

  const removeCategory = (category: string) => {
    if (!settings) return;
    updateSetting(
      'categories',
      settings.categories.filter((c) => c !== category)
    );

    const nextColors = { ...(settings.categoryColors || {}) };
    delete nextColors[category];
    updateSetting('categoryColors', nextColors);
  };

  const updateCategoryColor = (category: string, color: string) => {
    if (!settings) return;
    updateSetting('categoryColors', {
      ...(settings.categoryColors || {}),
      [category]: color,
    });
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-dark-400">Failed to load settings</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Settings</h1>
          <p className="text-dark-400 mt-1">Configure your application preferences</p>
        </div>
        
        <Button onClick={handleSave} disabled={!hasChanges || saving}>
          {saving ? (
            <LoadingSpinner size="sm" />
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      {hasChanges && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-4 py-3 text-yellow-400 text-sm">
          You have unsaved changes. Click "Save Changes" to apply them.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* General Settings */}
        <Card>
          <CardHeader className="flex flex-row items-center gap-3">
            <div className="w-10 h-10 bg-primary-500/20 rounded-xl flex items-center justify-center">
              <SettingsIcon className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">General</h2>
              <p className="text-dark-400 text-sm">Basic application settings</p>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Select
              label="Default Currency"
              value={settings.currency}
              onChange={(e) => updateSetting('currency', e.target.value)}
              options={currencyOptions}
            />
            
            <Select
              label="Date Format"
              value={settings.dateFormat}
              onChange={(e) => updateSetting('dateFormat', e.target.value)}
              options={dateFormats}
            />
            
            <Select
              label="Fiscal Year Start"
              value={settings.fiscalYearStart.toString()}
              onChange={(e) => updateSetting('fiscalYearStart', parseInt(e.target.value))}
              options={months}
            />
          </CardContent>
        </Card>

        {/* Appearance */}
        <Card>
          <CardHeader className="flex flex-row items-center gap-3">
            <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <Palette className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Appearance</h2>
              <p className="text-dark-400 text-sm">Customize the look and feel</p>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <label className="block text-sm font-medium text-dark-300">Theme</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => updateSetting('theme', 'dark')}
                  className={`p-4 rounded-lg border-2 transition-colors ${
                    settings.theme === 'dark'
                      ? 'border-primary-500 bg-primary-500/10'
                      : 'border-dark-600 bg-dark-700 hover:border-dark-500'
                  }`}
                >
                  <div className="w-full h-12 bg-dark-900 rounded-md mb-2" />
                  <span className="text-white text-sm">Dark</span>
                </button>
                <button
                  onClick={() => updateSetting('theme', 'light')}
                  className={`p-4 rounded-lg border-2 transition-colors ${
                    settings.theme === 'light'
                      ? 'border-primary-500 bg-primary-500/10'
                      : 'border-dark-600 bg-dark-700 hover:border-dark-500'
                  }`}
                >
                  <div className="w-full h-12 bg-white rounded-md mb-2" />
                  <span className="text-white text-sm">Light</span>
                </button>
              </div>
              <p className="text-dark-500 text-xs">
                Note: Light theme is available but currently optimized for dark mode.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Categories */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <h2 className="text-lg font-semibold text-white">Subscription Categories</h2>
            <p className="text-dark-400 text-sm mt-1">
              Manage categories for organizing your subscriptions
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2 mb-4">
              {settings.categories.map((category) => (
                <span
                  key={category}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-dark-700 text-dark-200 rounded-lg group"
                >
                  <input
                    type="color"
                    value={resolveCategoryColor(settings.categoryColors, category)}
                    onChange={(e) => updateCategoryColor(category, e.target.value)}
                    className="w-5 h-5 p-0 bg-transparent border-0 cursor-pointer"
                    title={`${category} color`}
                  />
                  {category}
                  <button
                    onClick={() => removeCategory(category)}
                    className="text-dark-400 hover:text-red-400 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </span>
              ))}
            </div>
            
            <div className="flex gap-3">
              <Input
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="New category name"
                className="flex-1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCategory();
                  }
                }}
              />
              <Button onClick={addCategory} disabled={!newCategory.trim()}>
                <Plus className="w-4 h-4" />
                Add
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* About */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <h2 className="text-lg font-semibold text-white">About</h2>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-white font-medium mb-2">Subscription Manager</h3>
                <p className="text-dark-400 text-sm">
                  A subscription tracking application to manage recurring expenses, view projections,
                  and organize subscriptions in one place.
                </p>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-dark-400">Version</span>
                  <span className="text-white">1.0.0</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-dark-400">Last Updated</span>
                  <span className="text-white">{settings.updatedAt ? new Date(settings.updatedAt).toLocaleDateString() : 'N/A'}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-dark-400">Support</span>
                  <span className="text-white break-all text-right">mshekhartp@gmail.com</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
